import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import {
	Alert,
	InputGroup,
	InputGroupAddon,
	InputGroupText,
	Row,
	Col,
	Button,
} from 'reactstrap';

import { useHistory, useLocation, Redirect } from "react-router-dom";

import { Formik, Field, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import axios from 'axios';
import TenantContext from '../../utils/tenantContext';

import SocialSignup from './socialSignup';

import LoaderInner from '../../components/LoaderInner';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import './signUp.css';

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const sidebarBackground = {
	backgroundRepeat: "no-repeat",
	backgroundPosition: "bottom center"
};

const linkStyle = {
	fontWeight: 600,
	color: "#2bcfe0"
};

const SignUp = () => {

  const history = useHistory();
  const location = useLocation();

  const inviteToken = location.pathname.split("/")[3];

  const { executeRecaptcha } = useGoogleReCaptcha();

  const formRef = useRef();

  const [ reCaptcha, setReCaptcha ] = useState('');

  const [isValidToken, setIsValidToken] = useState(false);
  const [invitationDetails, setInvitationDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const {tenant} = useContext(TenantContext);
  const [signUpEnabled, signUpEnabledSET] = useState(false);
  const [cookiePolicyURL, cookiePolicyURLSET] = useState("");
  const [privacyPolicyURL, privacyPolicyURLSET] = useState("");
  const [termsOfUsageURL, termsOfUsageURLSET] = useState("");
  const [redirect, setRedirect] = useState(false);

  const options = {
    headers: {
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };

  const validateToken = async (token) => {
	try {
	  const response = await axios.post(`${API_URL}validate-signup-invitation/`, { token });
	  if (response.status === 200) {
		let invitation = response.data.details;
		setInvitationDetails(invitation);
		setIsValidToken(true);
	  } else {
		console.log('Invalid token');
	  }
	} catch (error) {
	  console.error('Error validating token', error);
	} finally {
	  setIsLoading(false);
	}
  };  

  useEffect(() => {
    if (inviteToken) {
      validateToken(inviteToken);
    } else {
      setIsLoading(false);
    }
  }, [inviteToken]);

  useEffect(() => {
	if(Object.entries(tenant).length > 0) {
		const hasSignUpEnabled = tenant.hasOwnProperty('self_signup') && tenant.self_signup;
		if(hasSignUpEnabled) {
			signUpEnabledSET(true);
		} else {
			setRedirect(true);
		}
		cookiePolicyURLSET(tenant.cookie_policy_url);
		privacyPolicyURLSET(tenant.privacy_policy_url);
		termsOfUsageURLSET(tenant.terms_and_conditions_url);
	}
}, [tenant]);

	const handleReCaptchaVerify = useCallback(() => {
		if (!executeRecaptcha) {
			console.log("Recaptcha not ready yet");
			return;
		}

		(async () => {
			try {
				const token = await executeRecaptcha('authentication');
				setReCaptcha(token);
				if (formRef.current) {
					formRef.current.handleSubmit();
				}
			} catch (error) {
				console.error("Recaptcha execution failed:", error);
			}
		})();
	}, [executeRecaptcha]);

	const handleKeyPress = (event) => {
		if (event.key === 'Enter') {
		  handleReCaptchaVerify();
		}
	};

  if (isLoading) {
    return <div></div>;
  }

  if (inviteToken && !isValidToken) {
	history.push('/authentication/login');
  }

  return (
	<>
		{redirect ? <Redirect to="/authentication/login" /> : null}
		{signUpEnabled && (
			<div className="auth-wrapper d-flex align-items-center" style={sidebarBackground}>
				<div className="container">
					<div id="signupform">
						<Row className="no-gutters justify-content-center">
							{
								(inviteToken && isValidToken) &&
								<Col md="12" lg="12">
									<div style={{margin: "0 15% 20px 15%"}}>
										<Alert style={{background: "#ff6666"}} >
											<span style={{color: "#ffffff"}}>Hey <b>{invitationDetails.first_name} {invitationDetails.last_name}</b> You have been invited to join <a href="https://www.music-link.io" rel="noopener noreferrer" target="_blank" style={{color: "#ffffff", fontWeight: 'bold'}}><b>Music Link Platform</b></a> Please proceed with signup and confirm your account to unleash the full potential of our services.&nbsp;
												<a href="https://www.music-link.io" rel="noopener noreferrer" target="_blank" style={{color: "#ffffff", fontWeight: 'bold'}}>
													<u>Learn more about Music Link here</u>
												</a>
											</span>
										</Alert>
									</div>
								</Col>
							}

							<Col md="6" lg="4" className="bg-ml text-white">
								<div className="p-4">
									<h2 className="display-6">Your Music<br /> 
									<span className="text-ml font-bold">Your way – heard worldwide</span></h2>
								</div>
							</Col>
							<Col md="6" lg="4" className="bg-white">
								<div className="p-4">
									<h3 className="font-medium mb-3">Register Your Account</h3>
									<Formik
										innerRef={formRef}
										initialValues={{
											first_name: invitationDetails? invitationDetails.first_name: '',
											last_name: invitationDetails? invitationDetails.last_name: '',
											email: invitationDetails? invitationDetails.email: '',
											acceptTermsOfUsage: false,
											acceptPrivacyPolicy: false,
											acceptCookiePolicy: false,
										}}
										validationSchema={Yup.object().shape({
											first_name: Yup.string().required('First Name is required'),
											last_name: Yup.string().required('Last Name is required'),
											email: Yup.string()
											.required('Email is required')
											.email('Email is invalid')
											.test('noPlusSign', 'Email must not contain "+" symbol', function (value) {
												if (!value) return true;
												const localPart = value.split('@')[0];
												return !localPart.includes('+');
											})
											.test('checkEmailUnique', 'Email is already in use', async function (value) {
												if (!value || value === "") return true;
												try {
													const res = await axios.post(`${API_URL}users/validate-email/`, { email: value }, options);
													return res.data.available;
												} catch {
													return false;
												}
											}),
											acceptTermsOfUsage: Yup.bool().oneOf([true], 'Accepting Terms of Usage is required'),
											acceptPrivacyPolicy: Yup.bool().oneOf([true], 'Accepting Privacy Policy is required'),
											acceptCookiePolicy: Yup.bool().oneOf([true], 'Accepting Cookie Policy is required'),
										})}
										validateOnChange={false}
										validateOnBlur={true} 
										onSubmit={({ first_name, last_name, email }, { setStatus, setSubmitting }) => {
											setStatus();

											const reCaptchaPayload = {
												recaptcha: reCaptcha,
											};

											axios.post(`${API_URL}validate-google-recaptcha/`, reCaptchaPayload, options)
											.then(response => {
												const resData = response.data;
												if (resData && resData.valid) {
													const payload = {
														...(inviteToken && { token: inviteToken }),
														first_name: first_name,
														last_name: last_name,
														email: email,
													};

													axios.post(`${API_URL}user-register/`, payload, options).then(res => {
														if (res.status === 201) {
															history.push('/authentication/signup-successfull');
														}
														else if (res.status === 400)
														{
															let error  = res.data.data.message;
															console.log(error);
															setSubmitting(false);
															setStatus('Something went wrong. Please try again.');
														}
														else {
															setSubmitting(false);
															setStatus('Something went wrong. Please try again.');
														}
													}).catch(function (error) {
														if (error.response) {
															if(error.response.status === 400)
															{
																let error_data = error.response.data;
																let error_msg = error_data.data.message;

																if(error_msg) {
																	setSubmitting(false);
																	setStatus(error_msg);
																}
																else
																{
																	setSubmitting(false);
																	setStatus('Something went wrong. Please try again.');
																}
															}
															else {
																setSubmitting(false);
																setStatus('Something went wrong. Please try again.');
															}
														} else if (error.request) {
															setSubmitting(false);
															setStatus('Something went wrong. Please try again.');
														} else {
															setSubmitting(false);
															setStatus('Something went wrong. Please try again.');
														}
													});
												} else {
													setSubmitting(false);
													setStatus('Something went wrong. Please try again.');
												}
											}).catch(error => {
												console.error("Recaptcha validation failed:", error);
												setSubmitting(false);
												setStatus('Something went wrong. Please try again.');
											});
										}}
										render={({ errors, status, touched, isSubmitting }) => (
											<Form className="mt-3" id="signup-form">
												<LoaderInner show={isSubmitting}/>
												<InputGroup className="mb-3">
													<InputGroupAddon addonType="prepend">
														<InputGroupText>
															<i className="ti-user"></i>
														</InputGroupText>
													</InputGroupAddon>

													<Field name="first_name" placeholder="First Name" type="text" className={'form-control' + (errors.first_name && touched.first_name ? ' is-invalid' : '')} onKeyPress={handleKeyPress} />
													<ErrorMessage name="first_name" component="div" className="invalid-feedback" />
												</InputGroup>
												<InputGroup className="mb-3">
													<InputGroupAddon addonType="prepend">
														<InputGroupText>
															<i className="ti-user"></i>
														</InputGroupText>
													</InputGroupAddon>

													<Field name="last_name" placeholder="Last Name" type="text" className={'form-control' + (errors.last_name && touched.last_name ? ' is-invalid' : '')} onKeyPress={handleKeyPress} />
													<ErrorMessage name="last_name" component="div" className="invalid-feedback" />
												</InputGroup>

												<InputGroup className="mb-3">
													<InputGroupAddon addonType="prepend">
														<InputGroupText>
															<i className="ti-email"></i>
														</InputGroupText>
													</InputGroupAddon>

													<Field name="email" placeholder="Email" type="text" className={'form-control' + (errors.email && touched.email ? ' is-invalid' : '')} onKeyPress={handleKeyPress} />
													<ErrorMessage name="email" component="div" className="invalid-feedback" />
												</InputGroup>

												<div className="form-group">
													<div className="form-check form-check-inline">
														<Field type="checkbox" name="acceptTermsOfUsage" className={'form-check-input' + (errors.acceptTermsOfUsage && touched.acceptTermsOfUsage ? ' is-invalid' : '')}  />
														<label className="form-check-label" htmlFor="acceptTermsOfUsage">
															I accept the &nbsp;<a style={linkStyle} href={termsOfUsageURL} target='_blank' rel='noopener noreferrer'>Terms of Usage</a>
															<ErrorMessage name="acceptTermsOfUsage" component="div" className="invalid-feedback" />
														</label>
													</div>

													<div className="form-check form-check-inline">
														<Field type="checkbox" name="acceptPrivacyPolicy" className={'form-check-input' + (errors.acceptPrivacyPolicy && touched.acceptPrivacyPolicy ? ' is-invalid' : '')} />
														<label className="form-check-label" htmlFor="acceptPrivacyPolicy">
															I accept the &nbsp;<a style={linkStyle} href={privacyPolicyURL} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
															<ErrorMessage name="acceptPrivacyPolicy" component="div" className="invalid-feedback" />
														</label>
													</div>

													<div className="form-check form-check-inline">
														<Field type="checkbox" name="acceptCookiePolicy" className={'form-check-input' + (errors.acceptCookiePolicy && touched.acceptCookiePolicy ? ' is-invalid' : '')} />
														<label className="form-check-label" htmlFor="acceptCookiePolicy">
															I accept the &nbsp;<a style={linkStyle} href={cookiePolicyURL} target='_blank' rel='noopener noreferrer'>Cookie Policy</a>
															<ErrorMessage name="acceptCookiePolicy" component="div" className="invalid-feedback" />
														</label>
													</div>
												</div>

												<Row className="mb-3">
													<Col xs="12">
														<Button
															id="signup-submit"
															className="btn btn-ml"
															size="lg"
															// type="submit"
															block
															disabled={isSubmitting}
															onClick={handleReCaptchaVerify}
														>
															Sign Up
														</Button>
													</Col>
												</Row>

												{status &&
													<div className={'alert alert-danger new-line'}>{status}</div>
												}

												<div className="justify-content-center d-flex align-items-center">
													Signed Up Already?
													<a
														href="/authentication/login"
														className="forgot text-cyan float-right"
														style={{paddingLeft: 6}}
													>Login</a>
												</div>

												<hr/>
												<h4 className="text-center text-bold">Or</h4>
												<SocialSignup />
											</Form>
										)}
									/>
								</div>
							</Col>
						</Row>
					</div>
				</div>
			</div>
	    )}
  	</>
  );
};

export default SignUp;


